import type { PlatformServiceTicket } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  Cell,
  formatDateTime,
  Loading,
  PageHeader,
  Table,
} from '@avrahami/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { serviceEverywhereQuery } from '../api/platform';
import { TicketBadges } from '../components/ServiceTickets';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { ticketExplanation, ticketTitle } from '../lib/service';

function Row({ ticket }: { ticket: PlatformServiceTicket }) {
  const explanation = ticketExplanation(ticket);
  return (
    <tr data-testid="service-everywhere-row" data-tenant={ticket.tenantId}>
      <Cell>
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: ticket.tenantId }}
          className="font-medium text-brand-700 hover:underline"
        >
          {ticket.tenantName}
        </Link>
      </Cell>
      <Cell>
        <Link
          to="/tenants/$tenantId/service"
          params={{ tenantId: ticket.tenantId }}
          search={{ ticket: ticket.id }}
          className="font-medium text-slate-800 hover:text-brand-700 hover:underline"
        >
          {ticketTitle(ticket)}
        </Link>
        {explanation === null ? null : (
          <p className="mt-1 max-w-md text-xs text-slate-500">{explanation}</p>
        )}
      </Cell>
      <Cell>
        <TicketBadges ticket={ticket} />
      </Cell>
      <Cell className="whitespace-nowrap text-xs text-slate-600">
        {formatDateTime(ticket.updatedAt)}
      </Cell>
    </tr>
  );
}

/**
 * The faults and service calls still open in every store (adr/0036), the serious ones first:
 * one place to see what needs attention across the platform.
 */
export function ServicePage() {
  const list = useQuery(serviceEverywhereQuery);
  return (
    <>
      <PageHeader
        title={he.service.everywhereTitle}
        actions={
          <Button
            tone="secondary"
            disabled={list.isFetching}
            onClick={() => {
              void list.refetch();
            }}
          >
            {he.domains.refresh}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        <p className="max-w-3xl text-sm text-slate-600">{he.service.everywhereIntro}</p>
        {list.isPending ? <Loading /> : null}
        {list.isError ? <Alert>{errorMessage(list.error)}</Alert> : null}
        {list.data === undefined ? null : (
          <Card>
            {list.data.tickets.length === 0 ? (
              <p className="text-sm text-slate-600" data-testid="service-everywhere-empty">
                {he.service.everywhereEmpty}
              </p>
            ) : (
              <Table
                head={[
                  he.service.store,
                  he.service.subject,
                  he.service.statusLabel,
                  he.service.updated,
                ]}
              >
                {list.data.tickets.map((ticket) => (
                  <Row key={ticket.id} ticket={ticket} />
                ))}
              </Table>
            )}
            {list.data.truncated ? (
              <p className="mt-3 text-xs text-slate-500">{he.service.truncated}</p>
            ) : null}
          </Card>
        )}
      </div>
    </>
  );
}
